import { IFlightMarker } from "../../models/airports/AirportConfig";
import {
  BaggageData,
  DelayData,
  FlightDetailsData,
  IFlightDetails,
  PassengersData,
  ResourcesData,
  TimingData,
} from "../../models/flights/IFlightsCommon";
import { capitalizeFirst, joinCodes } from "../common/CommonHelperFunctions";
import { formatGroundHandler } from "./FlightHelperFunctions";

export interface TransformedFlightData {
  flightDetails: FlightDetailsData;
  timing: TimingData;
  passengers: PassengersData;
  baggage: BaggageData;
  delay: DelayData;
  resources: ResourcesData;
  flightMarker: IFlightMarker;
}

/** Formats total delay minutes into "HH:MM". Returns null if total is 0. */
export const formatDelayTime = (durations: string[]): string | null => {
  const totalMinutes = durations.reduce(
    (sum, d) => sum + (parseInt(d) || 0),
    0
  );
  if (totalMinutes <= 0) return null;

  const hh = Math.floor(totalMinutes / 60)
    .toString()
    .padStart(2, "0");
  const mm = (totalMinutes % 60).toString().padStart(2, "0");
  return `${hh}:${mm}`;
};

export const EMPTY_DATA: TransformedFlightData = {
  flightDetails: {
    flightNumber: null,
    origin: null,
    destination: null,
    status: null,
    remarkText: null,
    legType: null,
    flightType: null,
    airline: null,
    operatingCarrier: null,
    acType: null,
    acReg: null,
    progressPercentage: 0,
    linkedFlight: null,
    linkedFlightId: null,
  },
  timing: {
    sibt: null,
    eibt: null,
    cibt: null,
    tibt: null,
    pibt: null,
    aibt: null,
    aldt: null,
    sobt: null,
    eobt: null,
    cobt: null,
    tobt: null,
    pobt: null,
    aobt: null,
    atot: null,
    legType: null,
    bestInBlockTime: null,
    bestOffBlockTime: null,
  },
  passengers: {
    totalPassengers: null,
    capacity: null,
    booked: null,
    accepted: null,
    boarded: null,
    computed: null,
    transfer: null,
  },
  baggage: { totalBaggage: null, accepted: null, loaded: null },
  delay: {
    delayTime: null,
    delayCode: null,
    subDelayCode: null,
    delayDuration: null,
    serviceType: null,
    serviceCategory: null,
  },
  resources: {
    gate: null,
    stand: null,
    belt: null,
    chute: null,
    groundHandler: null,
  },
  flightMarker: {
    id: "",
    flightNumber: "",
    latitude: NaN,
    longitude: NaN,
    direction: NaN,
    legType: "",
    status: "" as any,
    docId: "",
    bestOffBlockTime: "",
    bestInBlockTime: "",
    callSign: "",
    aircraftRegistration: "",
    aircraftType: "",
  },
};

/** Transforms raw API response into component-ready data structures. */
export const transformFlightData = (
  apiData: IFlightDetails | null | undefined
): TransformedFlightData => {
  if (!apiData) return EMPTY_DATA;

  const durations = apiData.delayDurations ?? [];
  const delayTimeFormatted = formatDelayTime(durations);

  return {
    flightDetails: {
      flightNumber: apiData.flightNumber ?? null,
      origin: apiData.origin ?? "-",
      destination: apiData.destination ?? "-",
      status: apiData.operationalStatus ?? null,
      remarkText: apiData.remarkText ?? null,
      legType: capitalizeFirst(apiData.legType) ?? null,
      flightType: apiData.trafficType ?? null,
      airline: apiData.airlineName ?? null,
      operatingCarrier: apiData.operatingCarrier ?? null,
      acType: apiData.aircraftTypeName ?? null,
      acReg: apiData.aircraftRegistration ?? apiData.tailNumber ?? null,
      progressPercentage: apiData.progressPercentage ?? 0,
      linkedFlight: apiData.linkedFlightNumber ?? null,
      linkedFlightId: apiData.linkedFlightId ?? null,
    },
    timing: {
      sibt: apiData.sibt ?? null,
      eibt: apiData.eibt ?? null,
      cibt: apiData.cibt ?? null,
      tibt: apiData.tibt ?? null,
      pibt: apiData.pibt ?? null,
      aibt: apiData.aibt ?? null,
      aldt: apiData.aldt ?? null,
      sobt: apiData.sobt ?? null,
      eobt: apiData.eobt ?? null,
      cobt: null,
      tobt: apiData.tobt ?? null,
      pobt: apiData.pobt ?? null,
      aobt: apiData.aobt ?? null,
      atot: apiData.atot ?? null,
      legType: apiData.legType ?? null,
      bestInBlockTime: apiData.bestInBlockTime ?? null,
      bestOffBlockTime: apiData.bestOffBlockTime ?? null,
    },
    passengers: {
      totalPassengers: apiData.bookedPax ?? null,
      capacity: apiData.paxCapacity ?? null,
      booked: apiData.bookedPax ?? null,
      accepted: apiData.acceptedPax ?? null,
      boarded: apiData.boardedPax ?? null,
      computed: apiData.computedPax ?? null,
      transfer: apiData.transferPax ?? null,
    },
    baggage: {
      totalBaggage: apiData.loadedBaggage ?? null,
      accepted: apiData.acceptedBaggage ?? null,
      loaded: apiData.loadedBaggage ?? null,
    },
    delay: {
      delayTime: delayTimeFormatted,
      delayCode: joinCodes(apiData.delayCodes ?? []),
      subDelayCode: joinCodes(apiData.subDelayCodes ?? []),
      delayDuration: delayTimeFormatted,
      serviceType: apiData.serviceType ?? null,
      serviceCategory: apiData.serviceCategory ?? null,
    },
    resources: {
      gate: apiData.gate ?? null,
      stand: apiData.stand ?? null,
      belt: apiData.baggageBelt ?? null,
      chute: apiData.baggageChute ?? null,
      groundHandler: formatGroundHandler(apiData.groundHandler) || null,
    },
    flightMarker: {
      id: apiData.transponderAddress ?? "",
      flightNumber: apiData.flightNumber ?? "",
      latitude: apiData.latitude ?? NaN,
      longitude: apiData.longitude ?? NaN,
      direction: apiData.direction ?? NaN,
      legType: apiData.legType ?? "",
      status: (apiData.operationalStatus ?? "") as any,
      docId: apiData.id ?? "",
      bestOffBlockTime: apiData.bestOffBlockTime ?? "",
      bestInBlockTime: apiData.bestInBlockTime ?? "",
      callSign: apiData.callSign ?? "",
      aircraftRegistration: apiData.aircraftRegistration ?? "",
      aircraftType: apiData.aircraftTypeName ?? "",
    },
  };
};
